package com.advaita.cloud.drive.shared

import kotlinx.serialization.Serializable

/**
 * POST /api/archive/extract body — extract the archive [fileId]. With no destination the result
 * is a sibling of the archive. ADV-1529: [parentPath] (the caller's own space) or [parentId] (a
 * folder, possibly another person's) names another destination instead. At most one of the two.
 */
@Serializable
data class ExtractRequest(
    val fileId: String,
    val parentId: String? = null,
    val parentPath: String? = null,
)

/**
 * GET /api/archive/extract/destination answer (ADV-1529) — whether the caller may extract the
 * archive into the destination asked about (the archive's own folder when none is named).
 */
@Serializable
data class ExtractDestinationDto(val allowed: Boolean)

/** Response — the drive_file id of the (already created, still filling) destination folder. */
@Serializable
data class ExtractResponse(val destFolderId: String)

/**
 * POST /api/archive/compress body — bundle [fileIds] (files and/or folders) into one
 * archive called [name] in the folder the selection lives in. [format] is one of
 * `zip` | `tar.gz` | `tar` (see ArchiveFormat); the matching extension is appended to
 * [name] unless it is already there.
 */
@Serializable
data class CompressRequest(
    val fileIds: List<String>,
    val name: String,
    val format: String,
)

/** Response — the drive_file id of the (already created, still filling) archive row. */
@Serializable
data class CompressResponse(val outputFileId: String)

/**
 * Compact per-file archive-job progress folded onto [FileDto] for the file listing.
 * Rides on the dest folder (EXTRACT) or the output archive file (COMPRESS) — the job's
 * `target_file_id`. Totals are nullable because a tar stream's entry/byte count is
 * unknown until it has been read to the end; the UI renders indeterminate in that case
 * rather than a wrong 0%.
 */
@Serializable
data class ArchiveProgressDto(
    val status: String,          // PENDING | RUNNING | SUCCEEDED | FAILED
    val kind: String,            // EXTRACT | COMPRESS
    val entriesDone: Long = 0,
    val entriesTotal: Long? = null,
    val bytesDone: Long = 0,
    val bytesTotal: Long? = null,
    val error: String? = null,
)
